/** Whether the charts above a list are open - KD's design review, 2026-11-01, reversed by KD on
 * 2026-11-03 ("it doesn't have any dashboard elements in the pages").
 *
 * A list page's charts are **open by default**: the dashboard band is part of the page. A "Charts"
 * toggle at the right end of the filter line collapses them for a reader who wants the table higher,
 * and that choice is remembered. `localStorage` is a
 * per-viewer convenience and nothing more: it throws in a private window and with site data blocked,
 * so every access is inside a `try`, a failed read means "open" (the default), and a failed write
 * is dropped - the choice then lasts until the page is closed, a smaller loss than a page that does
 * not render.
 *
 * `TablePage` owns the state and provides it (`ChartsContext`); the `DashboardBand` slot and the
 * `FilterLine` read it, so the toggle and the thing it toggles cannot disagree and neither is passed
 * down by hand through a screen.
 */

import { createContext } from "react";

export const CHARTS_KEY = "emitiq.charts.open";

export function readChartsOpen(): boolean {
  try {
    // Only an explicit "closed" collapses them: an absent value is the default, open.
    return window.localStorage.getItem(CHARTS_KEY) !== "0";
  } catch {
    return true;
  }
}

export function writeChartsOpen(open: boolean): void {
  try {
    window.localStorage.setItem(CHARTS_KEY, open ? "1" : "0");
  } catch {
    // Blocked storage: the choice simply is not remembered.
  }
}

export interface ChartsSlot {
  /** Whether the page has any charts to open. A toggle for nothing is not drawn. */
  available: boolean;
  open: boolean;
  toggle: () => void;
}

export const ChartsContext = createContext<ChartsSlot | null>(null);
